/**
 * Staff Details - Edit Form Modal Component
 * Form for creating/editing staff details
 */
import { useState } from 'react';
import { 
  User, Phone, Briefcase, GraduationCap, Brain, Star, Zap, X, Save 
} from 'lucide-react';
import { SelectField, InputField, MultiSelectChips } from './FormComponents';
import { 
  VAKD_TYPES, DOPE_TYPES, COLOUR_BRAIN_TYPES, COLOUR_BRAIN_HANDLING_GUIDE,
  COMMUNICATION_STYLES, WORK_STYLES, LEADERSHIP_LEVELS, CONFLICT_STYLES,
  STRESS_LEVELS, MOTIVATION_TYPES, TEAM_ROLES, EMPLOYMENT_TYPES,
  EDUCATION_LEVELS, GENDERS, MARITAL_STATUSES, RACES, RELIGIONS, STATES,
  STRENGTH_OPTIONS, IMPROVEMENT_OPTIONS
} from './constants';

const EditFormModal = ({ staff, detail, onClose, onSave }) => {
  const [form, setForm] = useState({
    staff_id: staff?.id || '',
    full_name: detail?.full_name || staff?.name || '',
    ic_number: detail?.ic_number || staff?.ic_number || '',
    date_of_birth: detail?.date_of_birth || '',
    gender: detail?.gender || '',
    marital_status: detail?.marital_status || '',
    nationality: detail?.nationality || 'Malaysian',
    race: detail?.race || '',
    religion: detail?.religion || '',
    phone: detail?.phone || staff?.phone || '',
    email: detail?.email || staff?.email || '',
    address: detail?.address || staff?.address || '',
    city: detail?.city || '',
    state: detail?.state || '',
    postcode: detail?.postcode || '',
    emergency_contact_name: detail?.emergency_contact?.name || '',
    emergency_contact_relationship: detail?.emergency_contact?.relationship || '',
    emergency_contact_phone: detail?.emergency_contact?.phone || '',
    employee_id: detail?.employee_id || '',
    department: detail?.department || staff?.department || '',
    position: detail?.position || staff?.position || '',
    employment_type: detail?.employment_type || '',
    join_date: detail?.join_date || staff?.join_date || '',
    confirmation_date: detail?.confirmation_date || '',
    status: detail?.status || staff?.status || 'Active',
    bank_name: detail?.bank_name || staff?.bank_name || '',
    bank_account: detail?.bank_account || staff?.bank_account || '',
    education_level: detail?.education_level || '',
    education_field: detail?.education_field || '',
    certifications: detail?.certifications || [],
    languages: detail?.languages || [],
    communication_style: detail?.personality?.communication_style || '',
    work_style: detail?.personality?.work_style || '',
    leadership_potential: detail?.personality?.leadership_potential || '',
    conflict_resolution: detail?.personality?.conflict_resolution || '',
    stress_handling: detail?.personality?.stress_handling || '',
    motivation_type: detail?.personality?.motivation_type || '',
    team_role: detail?.personality?.team_role || '',
    strengths: detail?.personality?.strengths || [],
    areas_for_improvement: detail?.personality?.areas_for_improvement || [],
    personality_notes: detail?.personality?.notes || '',
    // VAKD & DOPE
    vakd_type: detail?.personality?.vakd_type || '',
    vakd_notes: detail?.personality?.vakd_notes || '',
    dope_type: detail?.personality?.dope_type || '',
    dope_notes: detail?.personality?.dope_notes || '',
    // Colour Brain
    colour_brain: detail?.personality?.colour_brain || '',
    colour_brain_secondary: detail?.personality?.colour_brain_secondary || '',
    colour_brain_notes: detail?.personality?.colour_brain_notes || '',
    previous_positions: detail?.previous_positions || [],
    promotions: detail?.promotions || [],
    photo_url: detail?.photo_url || ''
  });
  
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('personal');
  
  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form, detail?.id);
      onClose();
    } catch (e) {
      alert('Gagal menyimpan: ' + e.message);
    } finally {
      setSaving(false);
    }
  };
  
  const sections = [
    { id: 'personal', label: 'Peribadi', icon: User },
    { id: 'contact', label: 'Perhubungan', icon: Phone },
    { id: 'employment', label: 'Pekerjaan', icon: Briefcase },
    { id: 'education', label: 'Pendidikan', icon: GraduationCap },
    { id: 'personality', label: 'Personaliti', icon: Brain },
    { id: 'colour_brain', label: 'Colour Brain', icon: Zap },
    { id: 'vakd_dope', label: 'VAKD & DOPE', icon: Star }
  ];
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
        data-testid="staff-edit-modal"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">
              {detail ? 'Edit' : 'Tambah'} Maklumat Staff
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg" data-testid="close-edit-modal">
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
          
          {/* Section Tabs */}
          <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
            {sections.map(sec => (
              <button
                key={sec.id}
                onClick={() => setActiveSection(sec.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                  activeSection === sec.id
                    ? 'bg-white text-blue-700'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
                data-testid={`section-tab-${sec.id}`}
              >
                <sec.icon className="w-4 h-4" />
                {sec.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Form Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeSection === 'personal' && (
            <div className="grid md:grid-cols-2 gap-4">
              <InputField label="Nama Penuh" value={form.full_name} onChange={v => setForm({...form, full_name: v})} />
              <InputField label="No. IC" value={form.ic_number} onChange={v => setForm({...form, ic_number: v})} />
              <InputField label="Tarikh Lahir" type="date" value={form.date_of_birth} onChange={v => setForm({...form, date_of_birth: v})} />
              <SelectField label="Jantina" value={form.gender} onChange={v => setForm({...form, gender: v})} options={GENDERS} />
              <SelectField label="Status Perkahwinan" value={form.marital_status} onChange={v => setForm({...form, marital_status: v})} options={MARITAL_STATUSES} />
              <InputField label="Kewarganegaraan" value={form.nationality} onChange={v => setForm({...form, nationality: v})} />
              <SelectField label="Bangsa" value={form.race} onChange={v => setForm({...form, race: v})} options={RACES} />
              <SelectField label="Agama" value={form.religion} onChange={v => setForm({...form, religion: v})} options={RELIGIONS} />
            </div>
          )}
          
          {activeSection === 'contact' && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <InputField label="No. Telefon" value={form.phone} onChange={v => setForm({...form, phone: v})} />
                <InputField label="Email" type="email" value={form.email} onChange={v => setForm({...form, email: v})} />
              </div>
              <InputField label="Alamat" value={form.address} onChange={v => setForm({...form, address: v})} />
              <div className="grid md:grid-cols-3 gap-4">
                <InputField label="Bandar" value={form.city} onChange={v => setForm({...form, city: v})} />
                <SelectField label="Negeri" value={form.state} onChange={v => setForm({...form, state: v})} options={STATES} />
                <InputField label="Poskod" value={form.postcode} onChange={v => setForm({...form, postcode: v})} />
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-700 mb-3">Maklumat Kecemasan</h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <InputField label="Nama" value={form.emergency_contact_name} onChange={v => setForm({...form, emergency_contact_name: v})} />
                  <InputField label="Hubungan" value={form.emergency_contact_relationship} onChange={v => setForm({...form, emergency_contact_relationship: v})} />
                  <InputField label="No. Telefon" value={form.emergency_contact_phone} onChange={v => setForm({...form, emergency_contact_phone: v})} />
                </div>
              </div>
            </div>
          )}
          
          {activeSection === 'employment' && (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <InputField label="ID Pekerja" value={form.employee_id} onChange={v => setForm({...form, employee_id: v})} />
                <InputField label="Jabatan" value={form.department} onChange={v => setForm({...form, department: v})} />
                <InputField label="Jawatan" value={form.position} onChange={v => setForm({...form, position: v})} />
                <SelectField label="Jenis Pekerjaan" value={form.employment_type} onChange={v => setForm({...form, employment_type: v})} options={EMPLOYMENT_TYPES} />
                <InputField label="Tarikh Mula" type="date" value={form.join_date} onChange={v => setForm({...form, join_date: v})} />
                <InputField label="Tarikh Pengesahan" type="date" value={form.confirmation_date} onChange={v => setForm({...form, confirmation_date: v})} />
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-700 mb-3">Maklumat Bank</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <InputField label="Nama Bank" value={form.bank_name} onChange={v => setForm({...form, bank_name: v})} />
                  <InputField label="No. Akaun" value={form.bank_account} onChange={v => setForm({...form, bank_account: v})} />
                </div>
              </div>
            </div>
          )}
          
          {activeSection === 'education' && (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <SelectField label="Tahap Pendidikan" value={form.education_level} onChange={v => setForm({...form, education_level: v})} options={EDUCATION_LEVELS} />
                <InputField label="Bidang Pengajian" value={form.education_field} onChange={v => setForm({...form, education_field: v})} />
              </div>
              
              <MultiSelectChips 
                label="Bahasa" 
                selected={form.languages} 
                options={['Bahasa Malaysia', 'English', 'Mandarin', 'Tamil', 'Arabic']}
                onChange={v => setForm({...form, languages: v})}
              />
            </div>
          )}
          
          {activeSection === 'personality' && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <SelectField label="Gaya Komunikasi" value={form.communication_style} onChange={v => setForm({...form, communication_style: v})} options={COMMUNICATION_STYLES} />
                <SelectField label="Gaya Kerja" value={form.work_style} onChange={v => setForm({...form, work_style: v})} options={WORK_STYLES} />
                <SelectField label="Potensi Kepimpinan" value={form.leadership_potential} onChange={v => setForm({...form, leadership_potential: v})} options={LEADERSHIP_LEVELS} />
                <SelectField label="Penyelesaian Konflik" value={form.conflict_resolution} onChange={v => setForm({...form, conflict_resolution: v})} options={CONFLICT_STYLES} />
                <SelectField label="Pengendalian Tekanan" value={form.stress_handling} onChange={v => setForm({...form, stress_handling: v})} options={STRESS_LEVELS} />
                <SelectField label="Jenis Motivasi" value={form.motivation_type} onChange={v => setForm({...form, motivation_type: v})} options={MOTIVATION_TYPES} />
              </div>
              
              <SelectField label="Peranan Dalam Pasukan" value={form.team_role} onChange={v => setForm({...form, team_role: v})} options={TEAM_ROLES} />
              
              <MultiSelectChips 
                label="Kekuatan" 
                selected={form.strengths} 
                options={STRENGTH_OPTIONS}
                onChange={v => setForm({...form, strengths: v})}
              />
              
              <MultiSelectChips 
                label="Perlu Diperbaiki" 
                selected={form.areas_for_improvement} 
                options={IMPROVEMENT_OPTIONS}
                onChange={v => setForm({...form, areas_for_improvement: v})}
              />
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nota Personaliti</label>
                <textarea
                  value={form.personality_notes}
                  onChange={e => setForm({...form, personality_notes: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nota tambahan mengenai personaliti staff..."
                />
              </div>
            </div>
          )}
          
          {activeSection === 'colour_brain' && (
            <div className="space-y-6">
              {/* Colour Brain Section */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">🎨</span>
                  <div>
                    <h3 className="font-bold text-purple-800">Colour Brain - Cara Otak Berfikir & Process</h3>
                    <p className="text-xs text-purple-600">Pilih warna utama cara staff ini berfikir dan process maklumat</p>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-3 mb-4">
                  {COLOUR_BRAIN_TYPES.map(type => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setForm({...form, colour_brain: type.value})}
                      className={`p-4 rounded-xl text-left transition-all border-2 ${
                        form.colour_brain === type.value
                          ? `bg-gradient-to-br ${type.bgColor} text-white shadow-lg scale-[1.02] border-transparent`
                          : 'bg-white hover:bg-gray-50 border-gray-200'
                      }`}
                      data-testid={`colour-brain-${type.value.toLowerCase()}`}
                    >
                      <p className="font-bold text-lg mb-1">{type.label}</p>
                      <p className={`text-sm mb-2 ${form.colour_brain === type.value ? 'text-white/90' : 'text-gray-500'}`}>
                        {type.desc}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {type.traits.slice(0, 2).map((trait, i) => (
                          <span key={i} className={`px-2 py-0.5 rounded text-xs ${
                            form.colour_brain === type.value ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'
                          }`}>{trait}</span>
                        ))}
                      </div>
                    </button>
                  ))}
                </div>
                
                {/* Secondary Colour */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Colour (Optional)</label>
                  <div className="flex flex-wrap gap-2">
                    {COLOUR_BRAIN_TYPES.filter(t => t.value !== form.colour_brain).map(type => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setForm({...form, colour_brain_secondary: form.colour_brain_secondary === type.value ? '' : type.value})}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          form.colour_brain_secondary === type.value
                            ? `bg-gradient-to-br ${type.bgColor} text-white`
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nota Colour Brain</label>
                  <textarea
                    value={form.colour_brain_notes}
                    onChange={e => setForm({...form, colour_brain_notes: e.target.value})}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Contoh: Staff ni strong Red tapi ada Yellow influence bila deal dengan team..."
                  />
                </div>
              </div>
              
              {/* Colour Brain Info */}
              {form.colour_brain && (
                <div className="bg-white rounded-xl p-4 border border-gray-200">
                  <h4 className="font-bold text-gray-800 mb-3">Cara Handle {form.colour_brain} Brain</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-green-600">✓ Communication</p>
                      <ul className="text-xs text-gray-600 space-y-1">
                        {(COLOUR_BRAIN_HANDLING_GUIDE[form.colour_brain]?.communication || []).map((c, i) => (
                          <li key={i}>• {c}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-blue-600">📋 Instruction</p>
                      <ul className="text-xs text-gray-600 space-y-1">
                        {(COLOUR_BRAIN_HANDLING_GUIDE[form.colour_brain]?.instruction || []).map((c, i) => (
                          <li key={i}>• {c}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-amber-600">🎯 Motivation</p>
                      <ul className="text-xs text-gray-600 space-y-1">
                        {(COLOUR_BRAIN_HANDLING_GUIDE[form.colour_brain]?.motivation || []).map((c, i) => (
                          <li key={i}>• {c}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-red-600">⚠️ Jangan</p>
                      <ul className="text-xs text-gray-600 space-y-1">
                        {(COLOUR_BRAIN_HANDLING_GUIDE[form.colour_brain]?.warning || []).map((c, i) => (
                          <li key={i}>• {c}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500">
                      <strong>Best Tasks:</strong> {COLOUR_BRAIN_HANDLING_GUIDE[form.colour_brain]?.bestTasks?.join(', ')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {activeSection === 'vakd_dope' && (
            <div className="space-y-6">
              {/* VAKD Section */}
              <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">🧠</span>
                  <div>
                    <h3 className="font-bold text-cyan-800">VAKD - Cara Otak Terima & Proses Maklumat</h3>
                    <p className="text-xs text-cyan-600">Pilih cara staff ini paling mudah faham maklumat</p>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-3 mb-4">
                  {VAKD_TYPES.map(type => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setForm({...form, vakd_type: type.value})}
                      className={`p-4 rounded-xl text-left transition-all ${
                        form.vakd_type === type.value
                          ? 'bg-cyan-600 text-white shadow-lg scale-[1.02]'
                          : 'bg-white hover:bg-cyan-50 border border-gray-200'
                      }`}
                      data-testid={`vakd-${type.value.toLowerCase()}`}
                    >
                      <p className="font-bold text-lg mb-1">{type.label}</p>
                      <p className={`text-sm ${form.vakd_type === type.value ? 'text-cyan-100' : 'text-gray-500'}`}>
                        {type.desc}
                      </p>
                    </button>
                  ))}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nota VAKD</label>
                  <textarea
                    value={form.vakd_notes}
                    onChange={e => setForm({...form, vakd_notes: e.target.value})}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                    placeholder="Contoh: Staff ni lebih suka bila explain guna diagram..."
                  />
                </div>
              </div>
              
              {/* DOPE Section */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">🎯</span>
                  <div>
                    <h3 className="font-bold text-amber-800">DOPE - Cara Otak Bertindak & Buat Keputusan</h3>
                    <p className="text-xs text-amber-600">Pilih gaya tingkah laku dan keputusan staff ini</p>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-3 mb-4">
                  {DOPE_TYPES.map(type => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setForm({...form, dope_type: type.value})}
                      className={`p-4 rounded-xl text-left transition-all ${
                        form.dope_type === type.value
                          ? 'bg-amber-600 text-white shadow-lg scale-[1.02]'
                          : 'bg-white hover:bg-amber-50 border border-gray-200'
                      }`}
                      data-testid={`dope-${type.value.toLowerCase()}`}
                    >
                      <p className="font-bold text-lg mb-1">{type.label}</p>
                      <p className={`text-sm ${form.dope_type === type.value ? 'text-amber-100' : 'text-gray-500'}`}>
                        {type.desc}
                      </p>
                      <p className={`text-xs mt-1 ${form.dope_type === type.value ? 'text-amber-200' : 'text-amber-600'}`}>
                        <strong>Sesuai:</strong> {type.role}
                      </p>
                    </button>
                  ))}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nota DOPE</label>
                  <textarea
                    value={form.dope_notes}
                    onChange={e => setForm({...form, dope_notes: e.target.value})}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    placeholder="Contoh: Kalau bagi task, staff ni perlukan autonomy..."
                  />
                </div>
              </div>
              
              {/* Combination Tips */}
              {form.vakd_type && form.dope_type && (
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">💡</span>
                    <h4 className="font-bold text-purple-800">Kombinasi: {form.vakd_type} + {form.dope_type}</h4>
                  </div>
                  <p className="text-sm text-gray-600">
                    {form.vakd_type === 'Visual' && form.dope_type === 'Eagle' && 'Leader yang laju & strategic - bagi visual roadmap dengan target jelas'}
                    {form.vakd_type === 'Kinestetik' && form.dope_type === 'Peacock' && 'Sales lapangan, high energy - biar buat demo dan roleplay'}
                    {form.vakd_type === 'Digital' && form.dope_type === 'Owl' && 'System & process master - bagi data lengkap dan logic flow'}
                    {form.vakd_type === 'Audio' && form.dope_type === 'Dove' && 'Support role yang tenang & setia - brief secara lisan dengan appreciation'}
                    {!['Visual+Eagle', 'Kinestetik+Peacock', 'Digital+Owl', 'Audio+Dove'].includes(`${form.vakd_type}+${form.dope_type}`) && 
                      `Staff ${form.vakd_type} + ${form.dope_type}: Gunakan pendekatan ${form.vakd_type.toLowerCase()} untuk brief, dan motivate ikut style ${form.dope_type}.`
                    }
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="border-t p-4 flex justify-end gap-3 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            data-testid="cancel-edit-btn"
          >
            Batal
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
            data-testid="save-staff-details-btn"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Menyimpan...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Simpan
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditFormModal;
